/**
 * ComplianceSubNav - TASK-229
 * Sub-tab navigation for compliance sections
 */

import React from 'react';
import {
  LayoutDashboard,
  FileText,
  Search,
  ArrowLeftRight,
  Gauge,
  ClipboardCheck,
  type LucideIcon,
} from 'lucide-react';
import { useAccount } from '../../contexts/AccountContext';

export type ComplianceSubTab =
  | 'overview'
  | 'forms'
  | 'screening'
  | 'transactions'
  | 'risk-rating'
  | 'review'; // TASK-374: CO Review sub-tab

interface SubTabConfig {
  id: ComplianceSubTab;
  label: string;
  icon: LucideIcon;
  coOnly?: boolean; // TASK-374: Only visible to CO
}

const subTabs: SubTabConfig[] = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'review', label: 'Review Queue', icon: ClipboardCheck, coOnly: true }, // TASK-374
  { id: 'forms', label: 'Forms', icon: FileText },
  { id: 'screening', label: 'Screening', icon: Search },
  { id: 'transactions', label: 'Transactions', icon: ArrowLeftRight },
  { id: 'risk-rating', label: 'Risk Rating', icon: Gauge },
];

interface ComplianceSubNavProps {
  activeTab: ComplianceSubTab;
  onTabChange: (tab: ComplianceSubTab) => void;
}

export const ComplianceSubNav: React.FC<ComplianceSubNavProps> = ({
  activeTab,
  onTabChange,
}) => {
  const { isComplianceOfficer, isAdmin } = useAccount();

  // TASK-374: Filter tabs based on role — CO sees review tab, others don't
  const visibleTabs = subTabs.filter((tab) => {
    if (tab.coOnly && !isComplianceOfficer && !isAdmin) return false;
    return true;
  });

  return (
    <nav className="flex border-b border-gray-200 -mb-px overflow-x-auto">
      {visibleTabs.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          onClick={() => onTabChange(id)}
          className={`
            flex items-center gap-2 py-3 px-4 border-b-2 font-medium text-sm
            whitespace-nowrap transition-colors
            ${
              activeTab === id
                ? 'border-kielis-gold text-kielis-navy'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }
          `}
        >
          <Icon className="w-4 h-4" />
          {label}
        </button>
      ))}
    </nav>
  );
};

export default ComplianceSubNav;
